import React from 'react';
import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useLatestCallbackRef } from '../../Hooks/useLatestCallbackRef';

export type ColumnProps = React.PropsWithChildren<{
    id: string | number;
    width: number;
    className?: string;
    onColumnBodyEnter?: () => void;
    onColumnBodyLeave?: () => void;
}>;

export type BlockProps = React.PropsWithChildren<{
    id: string | number;
}>;

const blocksHeightMap: Map<string | number, number> = new Map();

const noop = () => {};

export function Column({ id, width, className, children, onColumnBodyEnter, onColumnBodyLeave }: React.PropsWithChildren<ColumnProps>) {
    const onColumnBodyEnterRef = useLatestCallbackRef(onColumnBodyEnter ?? noop);
    const onColumnBodyLeaveRef = useLatestCallbackRef(onColumnBodyLeave ?? noop);

    const viewRef = useRef<Viewport>({
        width: window.innerWidth,
        height: window.innerHeight,
        scrollLeft: window.scrollX,
        scrollTop: window.scrollY,
    });

    const view = viewRef.current;

    const title = findChildOfType(children, Title);

    const blocks = findAllChildrenOfType(children, Block)
        .filter((x): x is React.ReactElement<BlockProps> => x != null)
        .reduce<React.ReactElement<BlockProps>[]>((visible, block) => {
            const height = blocksHeightMap.get(block.props.id) ?? 0;

            const y = visible.reduce((acc, child) => acc + (blocksHeightMap.get(child.props.id) ?? 0), 0);

            // TODO: create context and move 2 (buffer to context)
            if (y > view.scrollTop + view.height + 2 * height) {
                return visible;
            } else {
                return [...visible, block];
            }
        }, []);

    const [, setVisibleColumns] = useState<[]>([]);

    useEffect(() => {
        let ticking = false;

        const onScrollOrResize = () => {
            viewRef.current = {
                width: window.innerWidth,
                height: window.innerHeight,
                scrollLeft: window.scrollX,
                scrollTop: window.scrollY,
            };

            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    setVisibleColumns([]);
                    ticking = false;
                });
            }
        };

        window.addEventListener('scroll', onScrollOrResize);
        window.addEventListener('resize', onScrollOrResize);

        return () => {
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, []);

    return (
        <div
            key={id}
            className={`flex flex-col border border-b-0 border-deep-navy ${className ?? ''}`}
            style={{
                // left,
                width,
            }}
        >
            <div key={`col-title-${id}`}>{title}</div>
            <div onMouseEnter={onColumnBodyEnterRef.current} onMouseLeave={onColumnBodyLeaveRef.current}>
                {blocks.map((block) => (
                    <div
                        key={block.props.id}
                        ref={(node) => {
                            if (node?.offsetHeight) {
                                blocksHeightMap.set(block.props.id, node?.offsetHeight);
                            }
                        }}
                        className='left-0 box-border'
                    >
                        {block}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function Block({ children }: React.PropsWithChildren<BlockProps>) {
    return <>{children}</>;
}

export function Title({ children }: React.PropsWithChildren) {
    return <>{children}</>;
}

export const Grid = forwardRef(function VirtualGrid(
    { headerHeight, className = '', buffer = 1, children }: React.PropsWithChildren<VirtualGridProps>,
    ref: React.Ref<GridRef>,
) {
    const viewRef = useRef<Viewport>({
        width: window.innerWidth,
        height: window.innerHeight,
        scrollLeft: window.scrollX,
        scrollTop: window.scrollY,
    });

    const [, setVisibleColumns] = useState<[]>([]);

    const view = viewRef.current;

    const columns = findAllGridColumns(children)
        .filter((x): x is React.ReactElement<ColumnProps> => x != null)
        .reduce<React.ReactElement<ColumnProps>[]>((visible, column) => {
            const x = visible.reduce((acc, child) => acc + child.props.width, 0);

            if (x > view.scrollLeft + view.width + buffer * column.props.width) {
                return visible;
            } else {
                return [...visible, column];
            }
        }, []);

    useImperativeHandle(ref, () => ({
        invalidate: () => {
            setVisibleColumns([]);
        },
    }));

    useEffect(() => {
        let ticking = false;

        const onScrollOrResize = () => {
            viewRef.current = {
                width: window.innerWidth,
                height: window.innerHeight,
                scrollLeft: window.scrollX,
                scrollTop: window.scrollY,
            };

            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    setVisibleColumns([]);
                    ticking = false;
                });
            }
        };

        window.addEventListener('scroll', onScrollOrResize);
        window.addEventListener('resize', onScrollOrResize);

        return () => {
            window.removeEventListener('scroll', onScrollOrResize);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [buffer]);

    return (
        <div className={'flex relative h-full ' + className} style={{ width: '100%' }}>
            {columns}
        </div>
    );
});

export function ColumnHeader({ className = '', children }: React.PropsWithChildren<{ className?: string }>) {
    return <div className={'h-[38px] border border-deep-navy ' + className}>{children}</div>;
}

export type GridRef = { invalidate: () => void };

interface VirtualGridProps {
    // data: Column[];
    headerHeight: number;
    buffer?: number;
    className?: string;
}

interface Viewport {
    width: number;
    height: number;
    scrollLeft: number;
    scrollTop: number;
}

function findChildOfType(children: React.ReactNode, targetType: React.ComponentType) {
    let found: React.ReactElement | null = null;

    React.Children.forEach(children, (child) => {
        if (found || !React.isValidElement(child)) return;

        if (child.type === targetType) {
            found = child;
        } else if (child.props?.children) {
            found = findChildOfType(child.props.children, targetType);
        }
    });

    return found;
}

function findAllChildrenOfType<Props>(children: React.ReactNode, targetType: React.ComponentType<Props>) {
    const matches: (React.ReactElement | null)[] = [];

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return null;

        if (child.type === targetType) {
            matches.push(child);
        }

        if (child.props?.children) {
            matches.push(...findAllChildrenOfType(child.props.children, targetType));
        }
    });

    return matches;
}

const COLUMN_MARKER = Symbol('GridColumn');

export function createColumn<P>(Component: React.FC<P & ColumnProps>): React.FC<P & ColumnProps> {
    (Component as any).__gridColumnMarker = COLUMN_MARKER;
    return Component;
}

function isGridColumn(type: any): boolean {
    return type?.__gridColumnMarker === COLUMN_MARKER;
}

function findAllGridColumns(children: React.ReactNode) {
    const matches: React.ReactElement[] = [];

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        if (isGridColumn(child.type)) {
            matches.push(child);
        }

        if (child.props?.children) {
            matches.push(...findAllGridColumns(child.props.children));
        }
    });

    return matches;
}
